import {
  FilterBarContainer,
  FilterBarLeftContent,
  FilterBarRightContent,
  FiltersContainerLeftContainer,
  FiltersContainerRightContainer,
} from "./FilterBarTemplate.styled";
import {
  Select,
  Button,
  Autocomplete,
  SwitchMode,
  TabSwitcher,
} from "@ann/web-components";
import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const FilterBarTemplate = ({ 
  mode, 
  setMode, 
  autocompleteValue, 
  autocompleteKey, 
  onAutocompleteChange, 
  onAutocompleteClear 
}) => {
  const [searchTermType, setSearchTermType] = useState("");
  const [selectedType, setSelectedType] = useState([]);
  const [searchTermLastUpdate, setSearchTermLastUpdate] = useState("");
  const [selectedLastUpdate, setSelectedLastUpdate] = useState([]);
  const [searchTermOwner, setSearchTermOwner] = useState("");
  const [selectedOwner, setSelectedOwner] = useState([]);
  const [filtersKey, setFiltersKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine the initial selected tab based on the current route
  const getInitialTab = () => {
    if (location.pathname.includes('/templates')) {
      return "templates";
    }
    return "created";
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  // Update selected tab when location changes
  useEffect(() => {
    const newTab = getInitialTab();
    setSelectedTab(newTab);
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    if (tabId === "created") {
      navigate(`/v2/workflows`);
    } else if (tabId === "templates") {
      navigate(`/v2/workflows/templates`);
    } else if (tabId === "logs") {
      navigate(`/v2/workflows/logs`);
    }
  };

  const typeOptions = [
    { label: "Email", value: "email" },
    { label: "SMS", value: "sms" },
    { label: "Push Notification", value: "push" },
    { label: "Webhook", value: "webhook" },
    { label: "In-App", value: "in_app" },
    { label: "Slack", value: "slack" },
    { label: "Teams", value: "teams" },
  ];

  const lastUpdateOptions = [
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "This year", value: "this_year" },
  ];

  const ownerOptions = [
    { label: "John Doe", value: "john_doe" },
    { label: "Jane Smith", value: "jane_smith" },
    { label: "Mike Johnson", value: "mike_johnson" },
  ];

  const filteredType = useMemo(() => {
    if (!searchTermType.trim()) return typeOptions;
    const searchLower = searchTermType.toLowerCase();
    return typeOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermType]);

  const filteredLastUpdate = useMemo(() => {
    if (!searchTermLastUpdate.trim()) return lastUpdateOptions;
    const searchLower = searchTermLastUpdate.toLowerCase();
    return lastUpdateOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermLastUpdate]);

  const filteredOwner = useMemo(() => {
    if (!searchTermOwner.trim()) return ownerOptions;
    const searchLower = searchTermOwner.toLowerCase();
    return ownerOptions.filter(
      (owner) =>
        owner.label.toLowerCase().includes(searchLower) ||
        owner.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermOwner]);

  const handleMultipleSelectionType = (items) => {
    setSelectedType(items);
  };

  const handleSearchType = (searchValue) => {
    setSearchTermType(searchValue);
  };

  const handleMultipleSelectionLastUpdate = (items) => {
    setSelectedLastUpdate(items);
  };

  const handleSearchOwner = (searchValue) => {
    setSearchTermOwner(searchValue);
  };

  const handleMultipleSelectionOwner = (items) => {
    setSelectedOwner(items);
  };

  const handleClearAllFilters = () => {
    setSelectedType([]);
    setSelectedLastUpdate([]);
    setSelectedOwner([]);
    setSearchTermType("");
    setSearchTermLastUpdate("");
    setSearchTermOwner("");
    setFiltersKey((prev) => prev + 1);
  };

  const emptyOrValue = (arr) => (arr.length > 0 ? arr : undefined);

  return (
    <FilterBarContainer>
      <FilterBarLeftContent>
        <FiltersContainerLeftContainer >
          <TabSwitcher
            key={selectedTab}
            defaultSelectedId={selectedTab}
            size="medium"
            onChange={handleTabChange}
          >
            <TabSwitcher.Button id="created" label="Created" />
            <TabSwitcher.Button id="templates" label="Templates" />
            <TabSwitcher.Button id="logs" label="Audit Log" />
          </TabSwitcher>
        </FiltersContainerLeftContainer>
        <FiltersContainerRightContainer>
          <Autocomplete
            style={{ fontSize: "5px" }}
            key={autocompleteKey}
            value={autocompleteValue}
            onChange={onAutocompleteChange}
            onSearch={() => {}}
            onSuggestionSelect={() => {}}
            onClear={onAutocompleteClear}
            placeholder="Search Templates..."
            disabled={true}
            suggestions={[
              "Sales Pitch Template",
              "Customer Support Template",
              "Marketing Campaign Template",
              "Onboarding Template",
              "Lead Generation Template",
              "Follow-up Template",
              "Welcome Template",
              "Reminder Template",
            ]}
          />
          <Select
            key={`type-filter-${filtersKey}`}
            label="Type"
            placeholder="Type"
            onSelectionChange={handleMultipleSelectionType}
            size="small"
            value={emptyOrValue(selectedType)}
            width="auto"
            minWidth="139px"
            disabled={true}
          >
            <Select.Menu>
              {filteredType.map((type) => (
                <Select.Item
                  key={type.value}
                  value={type.value}
                  label={type.label}
                >
                  {type.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`last-update-filter-${filtersKey}`}
            label="Last Update"
            placeholder="Last Update"
            onSelectionChange={handleMultipleSelectionLastUpdate}
            size="small"
            value={emptyOrValue(selectedLastUpdate)}
            width="auto"
            minWidth="139px"
            disabled={true}
          >
            <Select.Menu>
              {filteredLastUpdate.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`owner-filter-${filtersKey}`}
            label="Owner"
            placeholder="Owner"
            onSelectionChange={handleMultipleSelectionOwner}
            size="small"
            value={emptyOrValue(selectedOwner)}
            width="auto"
            minWidth="144px"
            disabled={true}
          >
            <Select.Menu multipleChoice={true}>
              <Select.Search
                placeholder="Search a name"
                onChange={handleSearchOwner}
                label=""
              />
              {filteredOwner.map((owner) => (
                <Select.Item
                  key={owner.value}
                  value={owner.value}
                  label={owner.label}
                >
                  {owner.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Button
            icon=""
            iconLeft=""
            iconRight=""
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearAllFilters}
            disabled={true}
          />
        </FiltersContainerRightContainer>
      </FilterBarLeftContent>
      {setMode && (
        <FilterBarRightContent>
          <SwitchMode mode={mode} onModeChange={setMode} />
        </FilterBarRightContent>
      )}
    </FilterBarContainer>
  );
};

export default FilterBarTemplate;
